import React, { useState, useEffect, useRef } from 'react';
import classNames from 'classnames';
import { Placeholder } from '@sitecore-jss/sitecore-jss-nextjs';
import BackToTopButton from 'components/Generic/Buttons/BackToTopButton';
import styles from './styles.module.scss';
import { useSelector } from 'react-redux';
import { getHasPremiumArticleEloquaForm } from '@/store/articles';
import { ComponentProps } from 'lib/component-props';
import { usePageData } from 'lib/page-data/PageDataContext';
import { ArticleData } from 'model/Article';
import { useI18n } from 'next-localization';
import { useCalculatedArticleFields } from 'hooks/useCalculatedArticleField';
import useWindowDimensions from 'hooks/useWindowDimensions';
import { useSitecoreContext } from 'hooks/useSitecoreContext';
import findComponentInTree from 'lib/utils/findComponentInTree';

const useArticleDataAttributes = (
  article?: ArticleData
): Record<string, string | string[] | boolean | number | undefined | null> => {
  const { getEntityType, getArticleType, getLimitedCapabilities } = useCalculatedArticleFields();
  const { t } = useI18n();
  return article
    ? {
        'data-article_type': t(getArticleType(article.template)) ?? t('article'),
        'data-article_entity_type': getEntityType(article.template) ?? '',
        'data-article_tags': article.articleTags?.targetItems.map((t) => t.name?.value ?? '') ?? [],
        'data-article_category': article.articleCategory.targetItem?.name?.value ?? '',
        'data-article_limited_capabilities': getLimitedCapabilities(article) ?? [],
        'data-article_title': article.articleTitle.value ?? '',
        'data-article_strapline': article.articleStrapline.value ?? '',
        'data-article_publication_date': article.articlePublicationDate.formattedDateValue ?? '',
        'data-article_authors': article.articleAuthor.targetItems.map((a) => a.name.value) ?? [],
        'data-article_contributors':
          article.articleContributor.targetItems.map((c) => c.name?.value ?? '') ?? [],
        'data-article_content_type': article.contentGroup.targetItem?.name?.value ?? '',
        'data-article_is_premium': article.premiumContent?.boolValue ?? false,
        'data-article_themes': article.articleThemes?.targetItems.map(
          (theme) => theme.name?.value ?? ''
        ),
        'data-article_capabilities':
          article.articleCapabilities?.targetItems.map((c) => c.name.value) ?? [],
        'data-article_region': article.articleRegion.targetItem?.name?.value ?? '',
        'data-article_series': article.articleSeries.targetItem?.name?.value ?? '',
        'data-article_series_part': article.articleSeriesPart?.value ?? 0,
        'data-article_image': article.articleImage?.src ?? '',
        'data-article_duration': article.duration?.value ?? 0,
      }
    : {};
};

const ArticleBody: React.FC<ComponentProps> = ({ rendering }) => {
  const deviceType = useWindowDimensions();
  const { sitecoreContext } = useSitecoreContext();
  const [nextSteps, setNextSteps] = useState(false);
  const hasPremiumArticleEloquaForm = useSelector(getHasPremiumArticleEloquaForm);

  const articleBodyRef = useRef<HTMLDivElement | null>(null);
  const eloquaFormRef = useRef<HTMLDivElement | null>(null);
  const { articleData } = usePageData('article') ?? {};

  const dataAttributes = useArticleDataAttributes(articleData);

  useEffect(() => {
    const routeData = sitecoreContext.route;
    if (routeData?.placeholders['abrdn-main']) {
      const nextStep = findComponentInTree(routeData?.placeholders['abrdn-main'], 'NextSteps');
      setNextSteps(!!nextStep);
    }

    // Set premiumArticleHeight style variable
    if (articleBodyRef.current) {
      const premiumArticleHeight = articleBodyRef.current.clientHeight * 0.1;
      if (hasPremiumArticleEloquaForm) {
        articleBodyRef.current.setAttribute('tabindex', '-1');
      }
      document.documentElement.style.setProperty(
        '--premiumArticleHeight',
        `${premiumArticleHeight}px`
      );
    }

    // Push dataLayer event
    if (typeof window === 'undefined' || sitecoreContext.pageEditing) return;
    window?.dataLayer?.push({
      assetClass: sitecoreContext?.dataLayer?.content.article_capability || 'n/a',
    });
  }, [
    hasPremiumArticleEloquaForm,
    sitecoreContext?.dataLayer?.content.article_capability,
    sitecoreContext.pageEditing,
    sitecoreContext.route,
  ]);

  return (
    <div
      id={rendering.uid}
      data-e2e-component-uid={rendering.uid}
      // The class `data-attributes-container` is used by Sitecore search for finding the DOM element
      className={'container pt-5 pt-md-6 data-attributes-container'}
      {...dataAttributes}
    >
      <div
        className={classNames(
          'row d-flex justify-content-end flex-column flex-md-row',
          styles.articleBodyWrapper
        )}
      >
        {/* Left Column */}
        <div
          className={classNames(
            'col-12 col-md-12 col-lg-3 mt-6 mt-md-0',
            styles.abrdnArticleChaptersWrapper
          )}
        >
          <Placeholder name="abrdn-article-chapters" rendering={rendering} />
        </div>

        {/* Main Article Column */}
        <div className="col-12 col-md-8 col-lg-6">
          <Placeholder
            name="abrdn-article-key-highlights"
            className="mb-5 mb-md-6 mb-xl-8"
            rendering={rendering}
          />
          <Placeholder
            name="abrdn-article-intro"
            className="abrdn-article-intro d-flex flex-column mb-5"
            rendering={rendering}
          />
          <div
            className={classNames(styles.abrdnArticleBody, {
              [styles.hideArticleBody]: !sitecoreContext.pageEditing && hasPremiumArticleEloquaForm,
            })}
            ref={articleBodyRef}
          >
            <Placeholder
              name="abrdn-article-body"
              className={classNames('d-flex flex-column')}
              rendering={rendering}
              articleBody={true}
            />
            <Placeholder name="abrdn-article-footnote" rendering={rendering} />

            <Placeholder name="abrdn-article-next-steps" className="mt-5" rendering={rendering} />
          </div>
          <Placeholder
            ref={eloquaFormRef}
            name="abrdn-premium-article-eloqua-form"
            rendering={rendering}
          />
        </div>

        {/* Sidebar Column */}
        <div className="col-12 col-md-4 col-lg-3 mt-6 mt-md-0">
          <div className={styles.articleBodySidebar}>
            {deviceType !== 'mobile' && nextSteps && (
              <Placeholder name="abrdn-article-related-capabilities" rendering={rendering} />
            )}
            <Placeholder
              className="related-articles-placeholder"
              name="abrdn-article-related-stories"
              rendering={rendering}
            />
          </div>
        </div>
      </div>
      <BackToTopButton className="my-3 my-lg-4 d-flex justify-content-end" />
    </div>
  );
};

export default ArticleBody;
